import { type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes, forwardRef, useId, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const buttonStyles: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-ink hover:bg-brand-light active:bg-brand-600 shadow-sm',
  secondary: 'bg-white text-ink ring-1 ring-inset ring-ink/25 hover:bg-mint',
  ghost: 'text-ink hover:bg-mint',
  danger: 'bg-coral text-white hover:brightness-95',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-8 px-3.5 text-sm' : 'h-10 px-5 text-[15px]',
        buttonStyles[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

const controlBase =
  'rounded-lg border border-line bg-white px-3 text-[15px] text-text placeholder:text-muted/70 transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25 disabled:bg-canvas aria-[invalid=true]:border-coral';
/** Full width unless the caller gives a width (w-24, w-36, …); both in one class list would leave the winner to CSS order. */
const control = (className?: string) => cx(controlBase, !/(^|\s)w-/.test(className ?? '') && 'w-full');

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /** Small text at the right of the label, e.g. which languages are filled. */
  aside?: ReactNode;
  children: (id: string) => ReactNode;
  className?: string;
}

/** Label + control + hint/error. The control receives the generated id for `htmlFor`. */
export function Field({ label, hint, error, required, aside, children, className }: FieldProps) {
  const id = useId();
  return (
    <div className={cx('space-y-1.5', className)}>
      {(label || aside) && (
        <div className="flex items-baseline justify-between gap-3">
          {label && (
            <label htmlFor={id} className="text-sm font-medium text-ink">
              {required && <span className="mr-0.5 text-coral">*</span>}
              {label}
            </label>
          )}
          {aside && <span className="text-xs text-muted">{aside}</span>}
        </div>
      )}
      {children(id)}
      {error ? <p className="text-sm text-coral">{error}</p> : hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return <input ref={ref} aria-invalid={invalid || undefined} className={cx(control(className), 'h-10', className)} {...rest} />;
  },
);

/** Password box with an eye button that shows / hides what was typed. */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { invalid?: boolean }>(
  function PasswordInput({ className, invalid, ...rest }, ref) {
    const [shown, setShown] = useState(false);
    return (
      <div className="relative">
        <input ref={ref} type={shown ? 'text' : 'password'} aria-invalid={invalid || undefined} className={cx(control(className), 'h-10 pr-11', className)} {...rest} />
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? '隱藏密碼' : '顯示密碼'}
          aria-pressed={shown}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-lg text-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        >
          {shown ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
        </button>
      </div>
    );
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ className, invalid, rows = 4, ...rest }, ref) {
    return <textarea ref={ref} rows={rows} aria-invalid={invalid || undefined} className={cx(control(className), 'py-2', className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  function Select({ className, invalid, children, ...rest }, ref) {
    return (
      <select ref={ref} aria-invalid={invalid || undefined} className={cx(control(className), 'h-10 pr-8', className)} {...rest}>
        {children}
      </select>
    );
  },
);

export function Checkbox({ label, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cx('inline-flex cursor-pointer items-center gap-2 text-[15px] text-text', className)}>
      <input type="checkbox" className="size-4 rounded border-line accent-brand-600" {...rest} />
      {label}
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cx('inline-flex items-center gap-3 text-[15px]', disabled ? 'opacity-50' : 'cursor-pointer')}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx('relative h-6 w-11 rounded-full transition-colors', checked ? 'bg-brand' : 'bg-line')}
      >
        <span className={cx('absolute top-0.5 size-5 rounded-full bg-white shadow transition-all', checked ? 'left-[22px]' : 'left-0.5')} />
      </button>
      {label}
    </label>
  );
}

export function ColorInput({ value, onChange, id }: { value: string; onChange: (v: string) => void; id?: string }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label="colour picker"
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 cursor-pointer rounded-lg border border-line bg-white p-1"
      />
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} className="w-32 font-mono uppercase" maxLength={7} />
    </div>
  );
}
